import { useState } from "react";
import { Button, Input } from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { useToast } from "@/components/ui/Toast";
import { extractErrorMessage } from "@/api/client";
import { useFinalizePool } from "@/hooks/capitalMarkets/useSpvPools";
import type { FinalizePoolResponse, SpvPoolDto } from "@/types/spvPool";
import { isoDateInTz, useUserTimeZone } from "@/utils/datetime";

interface PoolFormationDialogProps {
  open: boolean;
  onClose: () => void;
  pool: SpvPoolDto;
  versionId: number;
}

/**
 * Payment System v3.0 PR 6 — pool formation ceremony dialog. The simplified
 * wizard surfaces the three steps inline: (1) review the impact, (2)
 * confirm by typing the pool name, (3) finalize. The CSS-document step
 * is rendered as a placeholder; CSS generation wiring lands in a follow-up
 * commit.
 */
export function PoolFormationDialog({
  open,
  onClose,
  pool,
  versionId,
}: PoolFormationDialogProps) {
  const toast = useToast();
  const tz = useUserTimeZone();
  const finalize = useFinalizePool(pool.id);

  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [confirm, setConfirm] = useState("");
  const [result, setResult] = useState<FinalizePoolResponse | null>(null);

  const today = isoDateInTz(new Date(), tz);

  const reset = () => {
    setStep(1);
    setConfirm("");
    setResult(null);
  };

  const handleClose = () => {
    onClose();
    setTimeout(reset, 200);
  };

  const handleFinalize = () => {
    finalize
      .mutateAsync({
        formationDate: today,
        expectedVersionId: versionId,
        confirmPoolName: confirm,
      })
      .then((res) => {
        setResult(res);
        toast.success(
          `Pool finalized — ${res.ssnsIssued} SSN${
            res.ssnsIssued === 1 ? "" : "s"
          } issued, ${res.rufObligationsFired} RUF + ${
            res.sftfObligationsFired
          } SFTF fired.`,
        );
      })
      .catch((err) => toast.error(extractErrorMessage(err)));
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      dismissOnBackdropClick={false}
      title={`Finalize ${pool.poolName}`}
      data-testid="pool-formation-dialog"
    >
      <div className="flex flex-col gap-space-3">
        <StepHeader step={step} />

        {step === 1 && (
          <div className="text-small text-gray-mid">
            <p className="m-0">
              Finalize is the atomic 3-step ceremony: contribute every loan's
              S1 segment, file the CSS, issue Seller Senior Notes (95% face),
              and fire Phoenix's RUF + SFTF recovery obligations.
              <strong className="text-onyx"> This action is irreversible.</strong>
            </p>
            <ul className="mt-space-2 mb-0 list-disc pl-space-4">
              <li>Each roster loan must have all 5 prerequisite events.</li>
              <li>One SSN per loan at face = 0.95 × segment principal.</li>
              <li>RUF (3% of loan principal) fires once per loan lifetime.</li>
              <li>SFTF (0.20% of loan principal) fires per contribution.</li>
            </ul>
          </div>
        )}

        {step === 2 && (
          <div className="flex flex-col gap-space-2">
            <p className="text-small text-gray-mid m-0">
              CSS document generation is wired separately. This step is a
              placeholder until the Reports v3.2 hook lands. Approve to
              continue.
            </p>
            <div className="rounded-md border border-border-light bg-white-warm p-space-3 text-small">
              <strong className="text-onyx">CSS Filing — TODO</strong>
              <br />
              The PoolFormationEvent will record <code>cssFiled = false</code>{" "}
              for now; CSS generation lands in a follow-up commit.
            </div>
          </div>
        )}

        {step === 3 && !result && (
          <div className="flex flex-col gap-space-2">
            <label className="text-small font-bold text-onyx">
              Type the pool name to confirm
            </label>
            <Input
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder={pool.poolName}
              data-testid="pool-formation-confirm-input"
            />
            <p className="text-small text-gray-mid m-0">
              Expected: <code className="text-onyx">{pool.poolName}</code>
            </p>
          </div>
        )}

        {step === 3 && result && (
          <div className="rounded-md border-2 border-status-success bg-white-warm p-space-3 text-small">
            <strong className="text-onyx">Pool finalized.</strong>
            <ul className="mt-space-2 mb-0 list-disc pl-space-4">
              <li>Roster loans: {result.rosterLoanCount}</li>
              <li>SSNs issued: {result.ssnsIssued}</li>
              <li>RUF obligations fired: {result.rufObligationsFired}</li>
              <li>SFTF obligations fired: {result.sftfObligationsFired}</li>
              <li>New pool version: {result.newVersionId}</li>
            </ul>
          </div>
        )}

        <div className="flex justify-end gap-space-2">
          {step > 1 && !result && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setStep((step - 1) as 1 | 2 | 3)}
            >
              Back
            </Button>
          )}
          {step < 3 && (
            <Button
              variant="primary"
              size="sm"
              onClick={() => setStep((step + 1) as 2 | 3)}
              data-testid="pool-formation-next"
            >
              Next
            </Button>
          )}
          {step === 3 && !result && (
            <Button
              variant="primary"
              size="sm"
              loading={finalize.isPending}
              disabled={confirm !== pool.poolName}
              onClick={handleFinalize}
              data-testid="pool-formation-finalize"
            >
              Finalize Pool
            </Button>
          )}
          {result && (
            <Button variant="primary" size="sm" onClick={handleClose}>
              Done
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
}

function StepHeader({ step }: { step: 1 | 2 | 3 }) {
  const labels = ["Review", "CSS Filing", "Confirm & Finalize"];
  return (
    <div className="flex items-center gap-space-2 text-small">
      {labels.map((label, idx) => {
        const n = (idx + 1) as 1 | 2 | 3;
        const active = n === step;
        const done = n < step;
        return (
          <div key={label} className="flex items-center gap-space-1">
            <span
              className={
                "inline-flex h-6 w-6 items-center justify-center rounded-full text-[11px] font-bold " +
                (active
                  ? "bg-gold text-onyx"
                  : done
                  ? "bg-status-success text-white"
                  : "bg-white-warm text-gray-mid border border-border-light")
              }
            >
              {n}
            </span>
            <span
              className={active ? "text-onyx font-bold" : "text-gray-mid"}
            >
              {label}
            </span>
            {idx < labels.length - 1 && (
              <span className="text-gray-mid mx-space-1">→</span>
            )}
          </div>
        );
      })}
    </div>
  );
}
